import FileCopyIcon from '@mui/icons-material/FileCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useMemo, useState, useCallback } from 'react';
import { Images } from '../assets';
import { ContentData } from '../interface';
import ClaimContainer from './ClaimContainer';

export interface SuccessContainerProps {
  claimStatus: string;
  deliveryWallet: string;
  viewTokenTrackerURL?: boolean;
  tokenTrackerURL: string;
  content?: ContentData;
  onClickSuccess?: () => void;
}

interface TransactionComponentProps {
  claimStatus: string;
  deliveryWallet: string;
}

const TransactionComponent = ({ claimStatus, deliveryWallet }: TransactionComponentProps) => {
  const theme = useTheme();

  const [isCopy, setIsCopy] = useState(false);

  const onClickCopy = useCallback(() => {
    navigator.clipboard.writeText(deliveryWallet);
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 2000);
  }, [deliveryWallet]);

  const address = useMemo(() => {
    return `${deliveryWallet?.substring(0, 6)}...${deliveryWallet?.substring(deliveryWallet.length - 4)}`;
  }, [deliveryWallet]);

  return (
    <Box
      sx={{
        padding: '20px 16px',
        backgroundColor: theme?.MojitoClaim?.deliveryBackgroundColor,
        border: `1px solid ${theme?.palette?.grey?.[200]}`,
        borderRadius: '4px',
        marginTop: '10px',
        marginBottom: '15px'
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '12px',
              color: theme?.MojitoClaim?.delivery?.textColor,
              fontFamily: theme?.MojitoClaim?.font?.tertiary
            }}
          >
            Delivery Status
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Typography
            sx={{
              color:
                claimStatus === 'Delivered'
                  ? theme?.MojitoClaim?.success
                  : theme?.MojitoClaim?.Discount?.inProgressColor,
              backgroundColor:
                claimStatus === 'Delivered'
                  ? theme?.MojitoClaim?.deliveryBackgroundColor
                  : theme?.MojitoClaim?.Discount?.inProgressBackground,
              width: '90px',
              display: 'flex',
              justifyContent: 'center',
              borderRadius: '100px',
              fontFamily: theme?.MojitoClaim?.font?.tertiary,
              fontWeight: 500,
              fontSize: '12px',
              padding: '3px'
            }}
          >
            {claimStatus}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '12px',
              color: theme?.MojitoClaim?.delivery?.textColor,
              fontFamily: theme?.MojitoClaim?.font?.tertiary
            }}
          >
            Delivery wallet
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: theme?.MojitoClaim?.delivery?.textColor,
              fontFamily: theme?.MojitoClaim?.font?.tertiary
            }}
          >
            {address}
          </Typography>
          <Typography onClick={onClickCopy}>
            <Tooltip title={isCopy ? 'Copied' : 'Copy'} placement="top-end">
              <FileCopyIcon
                sx={{
                  paddingLeft: '7px',
                  cursor: 'pointer',
                  color: theme?.MojitoClaim?.delivery?.textColor,
                  fontSize: '14px'
                }}
              />
            </Tooltip>
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const SuccessContainer = ({
  claimStatus,
  deliveryWallet,
  onClickSuccess,
  viewTokenTrackerURL,
  tokenTrackerURL,
  content
}: SuccessContainerProps) => {
  const theme = useTheme();

  return (
    <Box>
      <ClaimContainer
        image={content?.titleImageURL || Images.SUCCESS}
        title={content?.title || 'Successfully Claimed!'}
        description={
          content?.description || 'Your Member Pass has been successfully claimed. Please wait 30 minutes for delivery.'
        }
      />

      <TransactionComponent claimStatus={claimStatus} deliveryWallet={deliveryWallet} />
      <Button
        sx={{
          width: '100%',
          marginBottom: '20px',
          fontWeight: 500
        }}
        onClick={onClickSuccess}
      >
        {content?.buttonName || 'Finish'}
      </Button>
      {viewTokenTrackerURL && tokenTrackerURL && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <a
            rel="noreferrer"
            href={tokenTrackerURL}
            target="_blank"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textDecorationLine: 'none',
              fontFamily: theme?.MojitoClaim?.font?.secondary
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
                color: theme.MojitoClaim?.delivery?.tokenTrackerColor,
                fontFamily: theme?.MojitoClaim?.font?.secondary
              }}
            >
              View token tracker
            </Typography>
            <LaunchIcon
              sx={{
                marginLeft: '5px',
                color: theme.MojitoClaim?.delivery?.tokenTrackerColor,
                fontSize: '14px'
              }}
            />
          </a>
        </Box>
      )}
    </Box>
  );
};

export default SuccessContainer;
